import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './MenuBar.css';

const MenuBar = ({ 
  onSave, 
  onSaveAs, 
  onOpen,
  onUndo,
  onRedo,
  onCut,
  onCopy,
  onPaste,
  onPreferences,
  onViewHistory,
  onViewConnections,
  onViewWorksheets,
  currentView 
}) => {
  const [activeMenu, setActiveMenu] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMenu = (menu) => {
    setActiveMenu(activeMenu === menu ? null : menu);
  };

  const handleMenuClick = (action) => {
    action();
    setActiveMenu(null);
  };

  return (
    <div className="menu-bar" ref={menuRef}>
      <div className="menu-bar-items">
        {/* File Menu */}
        <div className="menu-item">
          <button 
            className={`menu-button ${activeMenu === 'file' ? 'active' : ''}`}
            onClick={() => toggleMenu('file')}
          >
            File
          </button>
          {activeMenu === 'file' && (
            <div className="menu-dropdown">
              <button 
                className="menu-option" 
                onClick={() => handleMenuClick(onSave)}
                title="Save current worksheet (Ctrl+S)"
              >
                <span className="menu-label">Save</span>
                <span className="menu-shortcut">Ctrl+S</span>
              </button>
              <button 
                className="menu-option" 
                onClick={() => handleMenuClick(onSaveAs)}
                title="Save worksheet with new name"
              >
                <span className="menu-label">Save As...</span>
                <span className="menu-shortcut">Ctrl+Shift+S</span>
              </button>
              <div className="menu-divider"></div>
              <button 
                className="menu-option" 
                onClick={() => handleMenuClick(onOpen)}
                title="Open saved worksheet"
              >
                <span className="menu-label">Open...</span>
                <span className="menu-shortcut">Ctrl+O</span>
              </button>
            </div>
          )}
        </div>

        {/* Edit Menu */}
        <div className="menu-item">
          <button 
            className={`menu-button ${activeMenu === 'edit' ? 'active' : ''}`}
            onClick={() => toggleMenu('edit')}
          >
            Edit
          </button>
          {activeMenu === 'edit' && (
            <div className="menu-dropdown">
              <button 
                className="menu-option" 
                onClick={() => handleMenuClick(onUndo)}
                title="Undo last action (Ctrl+Z)"
              >
                <span className="menu-label">Undo</span>
                <span className="menu-shortcut">Ctrl+Z</span>
              </button>
              <button 
                className="menu-option" 
                onClick={() => handleMenuClick(onRedo)}
                title="Redo last undone action (Ctrl+Y)"
              >
                <span className="menu-label">Redo</span>
                <span className="menu-shortcut">Ctrl+Y</span>
              </button>
              <div className="menu-divider"></div>
              <button 
                className="menu-option" 
                onClick={() => handleMenuClick(onCut)}
                title="Cut selected text (Ctrl+X)"
              >
                <span className="menu-label">Cut</span>
                <span className="menu-shortcut">Ctrl+X</span>
              </button>
              <button 
                className="menu-option" 
                onClick={() => handleMenuClick(onCopy)}
                title="Copy selected text (Ctrl+C)"
              >
                <span className="menu-label">Copy</span>
                <span className="menu-shortcut">Ctrl+C</span>
              </button>
              <button 
                className="menu-option" 
                onClick={() => handleMenuClick(onPaste)}
                title="Paste from clipboard (Ctrl+V)"
              >
                <span className="menu-label">Paste</span>
                <span className="menu-shortcut">Ctrl+V</span>
              </button>
              <div className="menu-divider"></div>
              <button 
                className="menu-option" 
                onClick={() => handleMenuClick(onPreferences)}
                title="Preferences"
              >
                <span className="menu-label">Preferences...</span>
              </button>
            </div>
          )}
        </div>

        {/* View Menu */}
        <div className="menu-item">
          <button 
            className={`menu-button ${activeMenu === 'view' ? 'active' : ''}`}
            onClick={() => toggleMenu('view')}
          >
            View
          </button>
          {activeMenu === 'view' && (
            <div className="menu-dropdown">
              <button 
                type="button"
                className="menu-option"
                aria-current={currentView === 'history' ? 'page' : undefined}
                onClick={() => handleMenuClick(onViewHistory)}
                title="View query history"
              >
                <span className="menu-label">Query History</span>
              </button>
              <button 
                type="button"
                className="menu-option"
                aria-current={currentView === 'connections' ? 'page' : undefined}
                onClick={() => handleMenuClick(onViewConnections)}
                title="View connections panel"
              >
                <span className="menu-label">Connections</span>
              </button>
              <button 
                type="button"
                className="menu-option menu-option-label-normal"
                aria-current={currentView === 'worksheets' ? 'page' : undefined}
                onClick={() => handleMenuClick(onViewWorksheets)}
                title="View worksheets"
              >
                <span className="menu-label">Worksheets</span>
              </button>
            </div>
          )}
        </div>

        {/* Account - inline with File, Edit, View, same style */}
        <div className="menu-item">
          <Link to="/account" className="menu-button menu-button-link" title="Account settings">
            Account
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MenuBar;
